import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Plus, Minus, Save, Loader2, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { Invoice, InvoiceItem, BillingService } from '@/types';
import { usePermission } from '@/hooks/usePermission';

// Types imported from @/types

interface InvoiceEditModalProps {
  invoice: (Invoice & { items: InvoiceItem[] }) | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoiceData: any) => Promise<void>;
  services: BillingService[];
  loading?: boolean;
}

export default function InvoiceEditModal({
  invoice,
  isOpen,
  onClose,
  onSave,
  services,
  loading = false
}: InvoiceEditModalProps) {
  const [editedInvoice, setEditedInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Initialize data when invoice changes
  useEffect(() => {
    if (invoice && isOpen) {
      setEditedInvoice({ ...invoice });
      setItems([...invoice.items]);
      setError(null);
    }
  }, [invoice, isOpen]);

  // Calculate totals — coerce to Number since API may return strings
  const subtotal = items.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0);
  const discountAmount = Number(editedInvoice?.discount_amount) || 0;
  const taxAmount = Number(editedInvoice?.tax_amount) || 0;
  const totalAmount = subtotal - discountAmount + taxAmount;
  const paidAmount = Number(editedInvoice?.paid_amount) || 0;
  const balanceAmount = totalAmount - paidAmount;

  // Invoice can be edited unless it is cancelled
  const canEdit = editedInvoice?.status !== 'cancelled';
  // Items can only be changed if no payment has been recorded yet
  const canEditItems = canEdit && Number(editedInvoice?.paid_amount) === 0;
  // Discount changes require billing:discount permission
  const { can } = usePermission();
  const canEditDiscount = canEdit && can('billing:discount');
  // A changed discount needs a reason, as when the bill was made
  const discountChanged = Math.abs(discountAmount - (Number(invoice?.discount_amount) || 0)) > 0.005;
  const needsReason = discountChanged && discountAmount > 0;

  if (!invoice || !editedInvoice) return null;

  const handleInputChange = (field: keyof Invoice, value: any) => {
    setEditedInvoice(prev => prev ? { ...prev, [field]: value } : null);
    setError(null);
  };

  const handleItemChange = (itemIndex: number, field: keyof InvoiceItem, value: any) => {
    setItems(prev => prev.map((item, index) => {
      if (index === itemIndex) {
        const updatedItem = { ...item, [field]: value };
        
        // Recalculate total price if quantity or unit price changes
        if (field === 'quantity' || field === 'unit_price') {
          // The line's own discount stays off its total
          updatedItem.total_price = Math.max(0, Number(updatedItem.quantity) * Number(updatedItem.unit_price) - (Number(updatedItem.discount_amount) || 0));
        }
        
        return updatedItem;
      }
      return item;
    }));
    setError(null);
  };

  const removeItem = (itemIndex: number) => {
    if (!canEditItems) {
      setError('Cannot modify items for paid or partial invoices');
      return;
    }
    setItems(prev => prev.filter((_, index) => index !== itemIndex));
  };

  const addService = (service: BillingService) => {
    if (!canEditItems) {
      setError('Cannot modify items for paid or partial invoices');
      return;
    }
    
    const newItem: InvoiceItem = {
      id: `temp-${Date.now()}`,
      service_id: service.id,
      service_name: service.name,
      category: service.category,
      quantity: 1,
      unit_price: service.price,
      total_price: service.price,
    };
    
    setItems(prev => [...prev, newItem]);
  };

  const validateAndSave = async () => {
    // Validation
    if (items.length === 0) {
      setError('Invoice must have at least one item');
      return;
    }

    if (items.some(item => !item.service_name.trim() || item.quantity <= 0 || item.unit_price < 0)) {
      setError('All items must have valid name, quantity, and price');
      return;
    }

    if (needsReason && !String(editedInvoice.discount_reason || '').trim()) {
      setError('Give a reason for the discount');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const invoiceData = {
        invoice_date:    editedInvoice.invoice_date,
        due_date:        editedInvoice.due_date || null,
        items: items.map(item => ({
          service_id:   item.service_id || null,
          service_name: item.service_name,
          category:     item.category,
          // Saved items come back from the API as decimal strings ("1.00"); the server only takes numbers
          quantity:     Number(item.quantity),
          unit_price:   Number(item.unit_price),
          total_price:  Number(item.total_price),
          discount_amount: Number(item.discount_amount) || 0,
          notes:        item.notes || null
        })),
        discount_amount: discountAmount,
        ...(discountChanged ? { discount_reason: discountAmount > 0 ? String(editedInvoice.discount_reason || '').trim() : null } : {}),
        tax_amount:      taxAmount,
        notes:           editedInvoice.notes || null,
      };

      await onSave(invoiceData);
      onClose();
    } catch (error: any) {
      console.error('Failed to save invoice:', error);
      setError(error.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-h-[90vh] overflow-hidden flex flex-col ${invoice.status !== 'cancelled' && Number(invoice.paid_amount) === 0 ? 'max-w-3xl' : 'max-w-lg'}`}>
        <DialogHeader className="border-b pb-3">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit className="w-4 h-4 text-primary" />
              Edit Invoice — {invoice.invoice_number}
            </div>
            {!canEdit && (
              <Badge variant="outline" className="bg-destructive/10 text-destructive text-xs">
                Read Only (Cancelled)
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Invoice Details */}
          <div className="flex-1 border-r p-3 space-y-3 overflow-y-auto">

            {/* Error Message */}
            {error && (
              <Alert className="status-critical">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Patient (read-only) */}
            <div className="px-3 py-2 bg-muted/30 rounded-lg text-sm space-y-0.5">
              <p className="font-medium text-foreground">{editedInvoice.patient_name || '—'}</p>
              {editedInvoice.patient_phone && (
                <p className="text-muted-foreground text-xs">{editedInvoice.patient_phone}</p>
              )}
            </div>

            {/* Financial Details */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1 text-muted-foreground">Discount (₹)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discountAmount}
                  onChange={(e) => handleInputChange('discount_amount', parseFloat(e.target.value) || 0)}
                  disabled={!canEditDiscount}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-muted-foreground">Tax (₹)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={taxAmount}
                  onChange={(e) => handleInputChange('tax_amount', parseFloat(e.target.value) || 0)}
                  disabled={!canEdit}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {needsReason && (
              <div>
                <label htmlFor="edit-discount-reason" className="block text-xs font-medium mb-1 text-muted-foreground">Discount reason</label>
                <Input
                  id="edit-discount-reason"
                  value={editedInvoice.discount_reason || ''}
                  onChange={(e) => handleInputChange('discount_reason', e.target.value)}
                  maxLength={200}
                  placeholder="e.g. Staff family, follow-up, senior citizen"
                  className="h-8 text-sm"
                />
              </div>
            )}

            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Notes</label>
              <Input
                value={editedInvoice.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                disabled={!canEdit}
                placeholder="Add any notes..."
                className="h-8 text-sm"
              />
            </div>

            {/* Invoice Items */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Items</h3>

              {/* Column headers */}
              <div className="grid grid-cols-12 gap-2 px-1 mb-1">
                <div className="col-span-4 text-xs text-muted-foreground">Service</div>
                <div className="col-span-2 text-xs text-muted-foreground">Qty</div>
                <div className="col-span-2 text-xs text-muted-foreground">Unit ₹</div>
                <div className="col-span-2 text-xs text-muted-foreground text-center">Total</div>
                <div className="col-span-2" />
              </div>

              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {items.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-4 border border-dashed rounded-md">
                    {canEditItems ? 'Click a service on the right to add items' : 'No items on this invoice'}
                  </div>
                )}
                {items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center border rounded-md px-2 py-1.5 bg-muted/20">
                    <div className="col-span-4">
                      <Input
                        value={item.service_name}
                        onChange={(e) => handleItemChange(index, 'service_name', e.target.value)}
                        disabled={!canEditItems}
                        placeholder="Service name"
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                        disabled={!canEditItems}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        disabled={!canEditItems}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="col-span-2 text-xs font-medium text-center">
                      ₹{(Number(item.total_price) || 0).toFixed(2)}
                    </div>
                    <div className="col-span-2 flex justify-center">
                      {canEditItems && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeItem(index)}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals Summary */}
            <div className="border-t pt-3 space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Discount</span>
                  <span>−₹{discountAmount.toFixed(2)}</span>
                </div>
              )}
              {taxAmount > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Tax</span>
                  <span>₹{taxAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold border-t pt-1.5">
                <span>Total</span>
                <span>₹{totalAmount.toFixed(2)}</span>
              </div>
              {paidAmount > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Paid</span>
                  <span>₹{paidAmount.toFixed(2)}</span>
                </div>
              )}
              {balanceAmount > 0 && (
                <div className="flex justify-between text-sm font-medium text-warning">
                  <span>Balance Due</span>
                  <span>₹{balanceAmount.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Available Services */}
          {canEditItems && (
            <div className="w-56 p-3 bg-muted/10 flex flex-col gap-2">
              <div>
                <h3 className="text-sm font-medium text-foreground">Services</h3>
                <p className="text-xs text-muted-foreground">Click to add</p>
              </div>
              <div className="space-y-1.5 overflow-y-auto flex-1">
                {services.filter(s => s.is_active).length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-4 border border-dashed rounded-md">
                    No services configured
                  </div>
                ) : (
                  services.filter(s => s.is_active).map(service => (
                    <div
                      key={service.id}
                      onClick={() => addService(service)}
                      className="p-2 border rounded-md cursor-pointer hover:bg-primary/5 hover:border-primary/30 transition-colors"
                    >
                      <div className="text-xs font-medium">{service.name}</div>
                      <div className="text-xs text-muted-foreground">{service.category}</div>
                      <div className="text-xs font-semibold text-primary">₹{service.price}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          {canEdit && (
            <Button size="sm" onClick={validateAndSave} disabled={saving || loading}>
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save Changes
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}